import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ModalSize = 'sm' | 'md' | 'lg'

interface Props {
  open: boolean
  title?: string
  children?: ReactNode
  actions?: ReactNode
  size?: ModalSize
  closable?: boolean
  onClose?: () => void
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.tabIndex !== -1,
  )
}

function focusPanel(panel: HTMLElement) {
  const focusables = getFocusables(panel)
  if (focusables.length > 0) {
    focusables[0]!.focus()
    return
  }
  panel.tabIndex = -1
  panel.focus()
}

export function Modal({
  open,
  title,
  children,
  actions,
  size = 'md',
  closable = true,
  onClose,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    previouslyFocused.current = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    if (panel) focusPanel(panel)

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable) {
        onClose?.()
        return
      }
      if (e.key !== 'Tab' || !panel) return

      const focusables = getFocusables(panel)
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }

      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      const active = document.activeElement

      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
      previouslyFocused.current?.focus()
    }
  }, [open, closable, onClose])

  if (!open) return null

  return createPortal(
    <div
      className='cf-modal-overlay'
      role='dialog'
      aria-modal
      aria-labelledby={title ? 'cf-modal-title' : undefined}
      onClick={(e) => {
        if (closable && e.target === e.currentTarget) onClose?.()
      }}
    >
      <div ref={panelRef} className={`cf-modal cf-modal--${size}`}>
        {(title || closable) && (
          <div className='cf-modal__header'>
            {title && (
              <h2 className='cf-modal__title' id='cf-modal-title'>
                {title}
              </h2>
            )}
            {closable && (
              <button
                className='cf-modal__close'
                type='button'
                aria-label='Fechar'
                onClick={onClose}
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className='cf-modal__body'>{children}</div>
        {actions && <div className='cf-modal__footer'>{actions}</div>}
      </div>
    </div>,
    document.body,
  )
}
